// src/types/review.ts

export type ReviewStatus = 'pending' | 'approved' | 'published' | 'rejected';

export type StayReviewActorType = 'guest' | 'host';
export type StayReviewTargetType = 'host_profile' | 'guest_profile';
export type StayReviewVisibility = 'private_pending' | 'public' | 'private_internal';

export const GUEST_STAY_CATEGORY_KEYS = [
  'accuracy',
  'cleanliness',
  'communication',
  'checkin',
  'location',
  'value',
] as const;
export type GuestStayCategoryKey = (typeof GUEST_STAY_CATEGORY_KEYS)[number];
export type GuestStayCategoryRatings = Record<GuestStayCategoryKey, number>;

export const GUEST_STAY_REVIEW_TAGS = [
  'great_value',
  'very_clean',
  'great_location',
  'smooth_checkin',
  'great_communication',
  'matches_listing',
] as const;
export type GuestStayReviewTag = (typeof GUEST_STAY_REVIEW_TAGS)[number];

export const HOST_GUEST_CATEGORY_KEYS = [
  'communication_clarity',
  'checkin_reliability',
  'rules_respect',
  'property_care',
  'checkout_compliance',
] as const;
export type HostGuestCategoryKey = (typeof HOST_GUEST_CATEGORY_KEYS)[number];
export type HostGuestCategoryRatings = Record<HostGuestCategoryKey, number>;

export const HOST_GUEST_INCIDENT_FLAGS = [
  'late_checkout',
  'noise_complaint',
  'rule_violation',
  'cleaning_overage',
  'minor_damage',
] as const;
export type HostGuestIncidentFlag = (typeof HOST_GUEST_INCIDENT_FLAGS)[number];

export type BookingReviewSideStatus =
  | 'not_started'
  | 'submitted'
  | 'published'
  | 'rejected'
  | 'no_submission';

export interface BookingReviewCycle {
  bookingId: string;
  profileId: string;
  hostUserId: string;
  guestUserId: string;
  guestEmail?: string;
  eligible: boolean;
  eligibilityCheckedAt: string;
  ineligibleReason?: string;
  opensAt: string;
  closesAt: string;
  guestReviewStatus: BookingReviewSideStatus;
  hostReviewStatus: BookingReviewSideStatus;
  guestSubmittedAt?: string;
  hostSubmittedAt?: string;
  publishedAt?: string;
  guestReviewInviteSentAt?: string;
  hostReviewInviteSentAt?: string;
  guestNudgesSent?: string[];
  hostNudgesSent?: string[];
  moderationStatus: 'clean' | 'flagged' | 'under_review' | 'resolved';
  moderationNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Review {
  id: string;
  profileId: string;
  reviewerId: string;
  reviewerName: string;
  reviewerTitle?: string;
  isVerifiedGuest?: boolean;
  verifiedBookingId?: string;
  rating: number; // 1–5
  body: string;
  status: ReviewStatus;
  createdAt: string; // ISO string (serialized from Firestore Timestamp for client transfer)
  actorType?: StayReviewActorType;
  targetType?: StayReviewTargetType;
  targetUserId?: string;
  visibility?: StayReviewVisibility;
  guestCategoryRatings?: Partial<GuestStayCategoryRatings>;
  hostCategoryRatings?: Partial<HostGuestCategoryRatings>;
  stayTags?: GuestStayReviewTag[];
  categoryRatings?: Record<string, number>;
  tags?: string[];
  wouldHostAgain?: boolean;
  incidentFlags?: HostGuestIncidentFlag[];
}
